<template>
  <card>
    <div slot="header" class="clinic-doctors__header">
      <span class="clinic-doctors__title">Clinic doctors</span>
      <span class="clinic-doctors__count" v-if="!loading">{{ doctors.length }} total</span>
    </div>

    <div v-if="loading" class="card-body">Loading doctors...</div>

    <div v-else-if="!doctors.length" class="card-body">
      No doctors linked to this clinic.
    </div>

    <table v-else class="table table-responsive clinic-doctors__table">
      <thead>
        <tr>
          <th>ID</th>
          <th></th>
          <th>Name</th>
          <th>Specialities</th>
          <th>Start of work</th>
          <th>Hidden</th>
          <th></th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="doctor in doctors" :key="doctor.id">
          <td>{{ doctor.id }}</td>
          <td>
            <img v-if="photoUrl(doctor)" :src="photoUrl(doctor)" class="clinic-doctors__ava" alt=""/>
          </td>
          <td>
            <a :href="`https://en.bookimed.com/doctor/${doctor.alias && doctor.alias.en}/`" target="_blank">
              {{ doctor.name && doctor.name.en ? doctor.name.en : '-' }}
            </a>
          </td>
          <td>{{ specialitiesText(doctor) }}</td>
          <td>{{ doctor.year_start_work || '-' }}</td>
          <td>{{ isHidden(doctor) ? 'Yes' : 'No' }}</td>
          <td>
            <button class="btn btn-primary btn-link" @click="openDoctor(doctor.id)">
              <i class="tim-icons icon-pencil"></i>
            </button>
          </td>
          <td>
            <button
              class="btn btn-danger btn-link"
              :disabled="deletingIds.has(doctor.id)"
              @click="handleDelete(doctor)"
            >
              <i class="tim-icons icon-trash-simple"></i>
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </card>
</template>

<script>
import { mapState } from 'vuex';
import APIDoctors from '@/api/modules/doctors';

export default {
  data: () => ({
    doctors: [],
    loading: false,
    deletingIds: new Set(),
  }),
  computed: {
    ...mapState('clinics', ['clinic']),
  },
  created() {
    // Fires only when the Doctors tab is opened for the first time
    // (el-tab-pane lazy keeps this component out of the DOM until then).
    this.loadDoctors();
  },
  methods: {
    async loadDoctors() {
      if (!this.clinic || !this.clinic.id) return;
      this.loading = true;
      try {
        const { data } = await APIDoctors.getDoctors({
          clinic_ids: [this.clinic.id],
          'per-page': 500,
        });
        this.doctors = data?.doctors || [];
      } catch (e) {
        this.$notify({
          type: 'danger',
          title: e?.response?.data?.error?.code || 'Failed to load doctors',
        });
      } finally {
        this.loading = false;
      }
    },
    photoUrl(doctor) {
      const photo = doctor.photo;
      if (!photo) return '';
      const hash = photo.slice(photo.lastIndexOf('/') + 1);
      return `https://img.bookimed.com/doctor/${hash}.jpeg`;
    },
    specialitiesText(doctor) {
      const names = (doctor.specialities || [])
        .map((s) => s?.name?.en)
        .filter(Boolean);
      return names.length ? names.join(', ') : '-';
    },
    isHidden(doctor) {
      const flag = (doctor.flags || []).find((f) => f.name === 'is_hide');
      return !!(flag && flag.value);
    },
    openDoctor(doctorId) {
      const routeData = this.$router.resolve({ name: 'Doctor', params: { doctorId } });
      window.open(routeData.href, '_blank');
    },
    async handleDelete(doctor) {
      const label = (doctor.name && doctor.name.en) || `#${doctor.id}`;
      try {
        await this.$confirm(`Delete doctor "${label}"?`, 'Warning', {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning',
        });
      } catch (e) {
        // user pressed Cancel
        return;
      }

      this.deletingIds.add(doctor.id);
      this.deletingIds = new Set(this.deletingIds);
      try {
        await APIDoctors.deleteDoctor(doctor.id);
        this.doctors = this.doctors.filter((d) => d.id !== doctor.id);
        this.$notify({ type: 'success', title: 'Doctor deleted' });
      } catch (e) {
        this.$notify({
          type: 'danger',
          title: e?.response?.data?.error?.code || 'Failed to delete doctor',
        });
      } finally {
        this.deletingIds.delete(doctor.id);
        this.deletingIds = new Set(this.deletingIds);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.clinic-doctors {
  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  &__title {
    font-size: 18px;
    font-weight: 600;
  }
  &__count {
    font-size: 13px;
    color: #888;
  }
  &__table {
    margin: 0 16px;
    th {
      background: #fafafa;
      font-size: 12px;
      text-transform: uppercase;
      color: #666;
    }
  }
  &__ava {
    width: 48px;
    height: 48px;
    object-fit: cover;
    border-radius: 4px;
  }
}
</style>
